import React, { useEffect, useState } from "react";
import api from "../api";
import { useLiveFeed } from "../useLiveFeed";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(1)} ${u[i]}`;
};

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/dashboard");
        if (mounted) setData(data);
      } catch (e) {
        // handled by interceptor
      }
    };
    tick();
    const id = setInterval(tick, 10_000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">Dashboard</h1>
        <div className="text-xs font-mono text-muted-foreground">
          {data?.ts ? `Last sync ${new Date(data.ts).toLocaleTimeString()}` : "…"}
        </div>
      </div>

      <div className="filmstrip-divider" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border">
        <Metric
          label="Jobs today"
          value={data?.jobs_today ?? "—"}
          chart={data?.jobs_last_24h}
        />
        <Metric
          label="Success rate"
          value={data ? `${data.success_rate_pct.toFixed(1)}%` : "—"}
          tone="success"
        />
        <Metric
          label="Queue size"
          value={data?.queue_size ?? "—"}
          live={!!data?.queue_size}
        />
        <Metric
          label="Errors / 1h"
          value={data?.errors_last_hour ?? "—"}
          tone={data?.errors_last_hour ? "danger" : undefined}
        />
        <Metric label="Total users" value={data?.total_users ?? "—"} />
        <Metric label="Pro users" value={data?.active_pro_users ?? "—"} tone="success" />
        <Metric
          label="S3 used"
          value={data ? fmtBytes(data.s3_used_bytes) : "—"}
        />
        <Metric
          label="Failed today"
          value={data?.jobs_failed_today ?? "—"}
          tone={data?.jobs_failed_today ? "danger" : undefined}
        />
      </div>

      <BgFullsourceSection bg={data?.bg_fullsource_24h} />

      <LiveSection />
    </div>
  );
}

function BgFullsourceSection({ bg }) {
  if (!bg) return null;
  const modeTone =
    bg.mode === "off" ? "danger" : bg.mode === "lazy" ? "success" : undefined;
  return (
    <div className="space-y-3 pt-2" data-testid="bg-fullsource-section">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-xl tracking-tight">
          Pro BG-Fullsource{" "}
          <span className="text-xs font-mono text-muted-foreground tracking-[0.18em] uppercase ml-2">
            // 24h
          </span>
        </h2>
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
          Sprint 4 · Phase 2
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border">
        <Metric
          label="Mode"
          value={bg.mode?.toUpperCase() ?? "—"}
          tone={modeTone}
        />
        <Metric
          label="Jobs · 24h"
          value={bg.total ?? 0}
          live={!!bg.inflight}
        />
        <Metric
          label="Done · 24h"
          value={bg.done ?? 0}
          tone={bg.done ? "success" : undefined}
        />
        <Metric
          label="Failed · 24h"
          value={bg.failed ?? 0}
          tone={bg.failed ? "danger" : undefined}
        />
        <Metric
          label="Bandwidth · 24h"
          value={fmtBytes(bg.bytes ?? 0)}
        />
      </div>
      {bg.avg_bytes > 0 && (
        <div className="text-[11px] font-mono text-muted-foreground">
          avg per job: {fmtBytes(bg.avg_bytes)}
          {bg.inflight > 0 && (
            <span className="ml-3">· in-flight: {bg.inflight}</span>
          )}
        </div>
      )}
    </div>
  );
}

function LiveSection() {
  const { events, connected } = useLiveFeed();
  return (
    <div className="space-y-4 pt-2" data-testid="live-feed">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-xl tracking-tight flex items-center gap-2">
          <span
            className={`live-dot inline-block w-2 h-2 rounded-full ${
              connected ? "bg-red-500" : "bg-muted-foreground"
            }`}
          />
          Live activity
        </h2>
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
          {connected ? "// streaming" : "// reconnecting"}
        </div>
      </div>
      <div className="border border-border bg-black/40 max-h-[360px] overflow-y-auto" data-testid="live-feed-list">
        {events.length === 0 && (
          <div className="px-4 py-12 text-center text-xs font-mono text-muted-foreground">
            // waiting for the next job…
          </div>
        )}
        {events.map((e) => (
          <LiveRow key={e._seq} ev={e} />
        ))}
      </div>
    </div>
  );
}

const STATUS_COLOR = {
  done: "text-emerald-400",
  failed: "text-red-400",
  canceled: "text-muted-foreground",
  expired: "text-muted-foreground",
  downloading: "text-amber-400",
  cutting: "text-amber-400",
  uploading_s3: "text-amber-400",
  sending_telegram: "text-amber-400",
  queued: "text-blue-400",
  probing: "text-blue-400",
};

function LiveRow({ ev }) {
  const ts = ev.ts ? new Date(ev.ts).toLocaleTimeString() : "";
  const userTag = ev.user
    ? `@${ev.user.username || ev.user.first_name || ev.user.telegram_user_id}`
    : "—";
  const url = ev.url || "";
  return (
    <div
      className="grid grid-cols-[80px_140px_140px_1fr] gap-3 px-4 py-2 border-b border-border last:border-b-0 font-mono text-xs items-center hover:bg-secondary/20 transition-colors"
      data-testid={`live-event-${ev.job_id}`}
    >
      <span className="text-muted-foreground">{ts}</span>
      <span className="truncate text-foreground/70">{userTag}</span>
      <span
        className={`uppercase tracking-[0.14em] text-[10px] ${
          STATUS_COLOR[ev.status] || "text-foreground"
        }`}
      >
        {ev.status}
      </span>
      <span className="truncate text-muted-foreground">{url}</span>
    </div>
  );
}

function Metric({ label, value, tone, live, chart }) {
  const toneCls =
    tone === "danger"
      ? "text-red-400"
      : tone === "success"
        ? "text-emerald-400"
        : "text-foreground";
  return (
    <div className="bg-background p-5 relative overflow-hidden" data-testid={`metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1.5">
        {live && (
          <span className="live-dot inline-block w-1.5 h-1.5 rounded-full bg-red-500" />
        )}
        {label}
      </div>
      <div className={`mt-3 font-mono text-3xl tracking-tight ${toneCls}`}>
        {value}
      </div>
      {chart && chart.length > 1 && (
        <div className="absolute right-2 bottom-2 left-1/2 h-12 opacity-50 pointer-events-none">
          <ResponsiveContainer>
            <LineChart data={chart}>
              <Line
                type="monotone"
                dataKey="count"
                stroke="hsl(var(--foreground))"
                strokeWidth={1}
                dot={false}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  fontSize: 11,
                  fontFamily: "JetBrains Mono",
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
